import React, { useState } from "react";
import { Button, message } from "antd";
import { AlertTriangle, Download, Loader, ShieldAlert, TerminalSquare } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { card, PanelHead, CodeBlock, CopyButton, InlineValue, Note } from "../sshProxyUi";
import {
  CONSOLE_ENV_FILE,
  CONSOLE_ENV_PATH,
  INSTALLER_ENV_PATH,
  downloadTextFile,
  proxyLiveness,
} from "../sshProxyModel";
import { downloadGatewayConfig } from "../../../../service/sshProxyService";

// Step 2 — Install the gateway and wait for its first heartbeat.
//
// Nothing is set up on targets here. The gateway reaches each target with a
// key issued for that one session, released after approval — targets are
// onboarded as endpoints, not through this wizard.
//
// The host-key fingerprint comes from listJumpServer, reported by the gateway on
// its heartbeat, and is null until then.

// A value the proxy has not reported yet. Never an empty code block: an empty
// block looks copyable and is not.
function Pending({ what }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 8,
        border: `1px dashed ${AN_COLORS.line}`, borderRadius: 7,
        padding: "10px 12px", background: AN_COLORS.bg,
        color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: 12.5,
      }}
    >
      <Loader size={14} />
      Waiting for the gateway to report {what}.
    </div>
  );
}


export default function Step2InstallKeys({ state, jumpServerId }) {
  const [messageApi, msgCtx] = message.useMessage();

  const live = proxyLiveness(state.lastSeenAt);
  const seen = live.state !== "never";

  const installCmd = `wget https://raw.githubusercontent.com/authnull0/windows-endpoint/main/sshproxy/install.sh
sudo bash install.sh`;

  // console.env comes from the server now — it carries the internal API key,
  // which the console has no way to compose.
  const [downloading, setDownloading] = useState(false);
  const downloadEnv = () => {
    setDownloading(true);
    downloadGatewayConfig({
      jumpServerId,
      onSuccess: (text) => {
        setDownloading(false);
        downloadTextFile(CONSOLE_ENV_FILE, typeof text === "string" ? text : String(text ?? ""));
      },
      onFailure: () => {
        setDownloading(false);
        messageApi.error("Couldn't download the configuration");
      },
    });
  };

  const installConfigCmd = `sudo cp ~/Downloads/${CONSOLE_ENV_FILE} ${CONSOLE_ENV_PATH}\nsudo systemctl restart authnull-sshproxy`;

  const heartbeatDebugCmd = `systemctl status authnull-sshproxy\njournalctl -u authnull-sshproxy -n 30`;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {msgCtx}
      {/* Panel 1 — install the binary. Always available. */}
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          eyebrow="Step 2 · Panel 1"
          title="Install the gateway"
          desc="The gateway runs as a systemd service on your jump box. After installing, tell it which jump-server row it is — it cannot discover that on its own."
          right={<span style={{ display: "inline-flex", color: AN_COLORS.muted }}><TerminalSquare size={18} /></span>}
        />
        <div style={{ display: "grid", gap: 12 }}>
          <CodeBlock copyText={installCmd} multiline>{installCmd}</CodeBlock>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Button
                type="primary"
                loading={downloading}
                disabled={!Number(jumpServerId)}
                onClick={downloadEnv}
                icon={<Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}
              >
                Download {CONSOLE_ENV_FILE}
              </Button>
              {!Number(jumpServerId) && (
                <span style={{ fontSize: 12, color: AN_COLORS.muted }}>
                  Available once Step 1 has created the gateway row.
                </span>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <CodeBlock copyText={installConfigCmd} multiline>{installConfigCmd}</CodeBlock>
            </div>

            {/* The installer's own file and ours are different files. Copying
                over the installer's destroys the HMAC key it
                generated, and the gateway keeps looking healthy afterwards. */}
            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
              <Note tone="danger" icon={<ShieldAlert size={15} />}>
                Copy it to <InlineValue>{CONSOLE_ENV_PATH}</InlineValue> — <strong>not</strong> over{" "}
                <InlineValue>{INSTALLER_ENV_PATH}</InlineValue>. That one belongs to the installer and holds the HMAC
                key it generated; overwriting it leaves recordings without their integrity sidecar, while the gateway
                carries on looking healthy. The unit reads both, ours second so it wins.
              </Note>
              <Note tone="warn" icon={<AlertTriangle size={15} />}>
                <strong>This file is a secret.</strong> It carries the gateway's internal API key — copy it to the jump
                box and delete your download. Run the copy <em>after</em> the installer:{" "}
                <InlineValue>/etc/authnull/sshproxy/</InlineValue> does not exist until then, so copying first fails
                loudly rather than leaving a file the service cannot read.
              </Note>
            </div>
          </div>
        </div>
      </div>

      {/* Heartbeat gate. */}
      {!seen ? (
        <div style={{ ...card, padding: 22, textAlign: "center" }}>
          <div style={{ display: "inline-flex", width: 44, height: 44, borderRadius: "50%", background: AN_COLORS.tint, alignItems: "center", justifyContent: "center", color: AN_COLORS.primary, marginBottom: 12 }}>
            <Loader size={20} />
          </div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: AN_COLORS.ink }}>Waiting for the gateway's first heartbeat</h3>
          <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 6, maxWidth: 560, marginInline: "auto", lineHeight: 1.55 }}>
            The gateway reports in once it is installed and configured. On the jump box: run the installer, download{" "}
            <InlineValue>{CONSOLE_ENV_FILE}</InlineValue> above and copy it into{" "}
            <InlineValue>{CONSOLE_ENV_PATH}</InlineValue>, then{" "}
            <InlineValue>sudo systemctl restart authnull-sshproxy</InlineValue>. This page polls and unlocks on its own.
          </div>
          {/* The gate is right, but on its own it leaves nobody anything to do.
              All three causes below are invisible from the console. */}
          <div style={{ marginTop: 18, textAlign: "left", maxWidth: 620, marginInline: "auto" }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: AN_COLORS.ink2, marginBottom: 8 }}>
              Not seeing a heartbeat?
            </div>
            <CodeBlock copyText={heartbeatDebugCmd} multiline>{heartbeatDebugCmd}</CodeBlock>
            <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.65 }}>
              <li>
                <strong>The service never started</strong> — almost always a missing{" "}
                <InlineValue>SSHPROXY_ORG_ID</InlineValue>. The log says so in one line.
              </li>
              <li>
                <strong><InlineValue>SSHPROXY_CONTROL_URL</InlineValue> unset</strong> — SSH works, the console never
                hears from it.
              </li>
              <li>
                <strong><InlineValue>SSHPROXY_INTERNAL_API_KEY</InlineValue> not matching</strong> the stack's{" "}
                <InlineValue>INTERNAL_API_KEY</InlineValue>.
              </li>
            </ul>
          </div>
        </div>
      ) : (
        <>
          {/* Panel 2 — host key fingerprint. */}
          <div style={{ ...card, padding: 18 }}>
            <PanelHead
              eyebrow="Step 2 · Panel 2"
              title="Publish the host-key fingerprint"
              desc="Give operators the gateway's own fingerprint so they can verify it on first connect instead of typing yes at a prompt they cannot check. This is a SHA256 fingerprint, not a key."
            />
            {state.hostKeyFingerprint ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontFamily: AN_MONO, fontSize: 13, background: AN_COLORS.tint, border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, padding: "10px 14px", color: AN_COLORS.ink, wordBreak: "break-all" }}>
                  {state.hostKeyFingerprint}
                </div>
                <CopyButton text={state.hostKeyFingerprint} label="Copy fingerprint" />
              </div>
            ) : (
              <Pending what="its host-key fingerprint" />
            )}
          </div>

        </>
      )}
    </div>
  );
}
